import { useState } from 'react';
import { X, Plus, Clock } from 'lucide-react';
import { Product, SubProduct } from '../types';
import { getFullImageUrl } from '../lib/api';
import { formatPreparationTime } from '../lib/format';

interface ProductDetailModalProps {
    product: Product;
    isOpen: boolean;
    onClose: () => void;
    onAdd: (product: Product, quantity: number, options: { sub_products: SubProduct[] }) => void;
    currencySymbol: string;
}

export function ProductDetailModal({ product, isOpen, onClose, onAdd, currencySymbol }: ProductDetailModalProps) {
    const [selectedGuarniciones, setSelectedGuarniciones] = useState<SubProduct[]>([]);

    if (!isOpen) return null;

    const handleAdd = () => {
        onAdd(product, 1, { sub_products: selectedGuarniciones });
        onClose();
        setSelectedGuarniciones([]);
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center animate-in fade-in duration-200">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-md" onClick={onClose} />
            <div className="relative bg-white dark:bg-[#161b2c] w-full max-w-lg rounded-t-[2.5rem] sm:rounded-[2.5rem] overflow-hidden shadow-2xl border border-white/10 animate-in slide-in-from-bottom-5 sm:zoom-in-95 duration-300">
                <div className="relative h-56 w-full">
                    <img src={getFullImageUrl(product.image)} alt={product.name} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#161b2c] via-transparent opacity-70" />
                    <button onClick={onClose} className="absolute top-4 right-4 p-2.5 bg-black/30 hover:bg-black/50 backdrop-blur-md rounded-2xl text-white border border-white/10 transition-all">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <div className="p-6 space-y-6 text-left">
                    <div className="flex justify-between items-start gap-3">
                        <h2 className="text-2xl font-black text-gray-900 dark:text-white leading-tight">{product.name}</h2>
                        <span className="text-2xl font-black text-orange-500 shrink-0">{currencySymbol}{product.price ? product.price.toFixed(0) : '0'}</span>
                    </div>
                    {product.preparation_time && product.preparation_time !== '00:00:00' && (
                        <div className="flex items-center gap-2 text-xs font-bold text-gray-400 uppercase tracking-wider">
                            <Clock className="w-3.5 h-3.5" />
                            <span>{formatPreparationTime(product.preparation_time)}</span>
                        </div>
                    )}
                    <p className="text-gray-500 dark:text-gray-400 text-sm leading-relaxed max-h-24 overflow-y-auto whitespace-pre-line">{product.description}</p>

                    {/* Side Dish Selector */}
                    {product.sub_products && product.sub_products.length > 0 && (
                        <div className="space-y-4 pt-2">
                            <div className="flex items-center justify-between">
                                <h3 className="text-sm font-black text-gray-900 dark:text-white uppercase tracking-[0.1em]">Elegí tu guarnición</h3>
                                {/* <span className="px-2 py-0.5 bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 text-[10px] font-black rounded-lg uppercase"></span> */}
                            </div>
                            <div className="flex gap-4 overflow-x-auto no-scrollbar pb-2 -mx-6 px-6">
                                {product.sub_products.map((sub: SubProduct) => {
                                    const isSelected = selectedGuarniciones.some(s => s.sub_product_uuid === sub.sub_product_uuid);
                                    return (
                                        <button
                                            key={sub.sub_product_uuid}
                                            onClick={() => {
                                                if (isSelected) {
                                                    setSelectedGuarniciones([]);
                                                } else {
                                                    setSelectedGuarniciones([sub]);
                                                }
                                            }}
                                            className={`relative flex-shrink-0 w-32 h-44 rounded-[2rem] overflow-hidden transition-all duration-300 border-2 ${isSelected
                                                ? 'border-orange-500 scale-105 shadow-xl shadow-orange-500/20'
                                                : 'border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800'
                                                }`}
                                        >
                                            <div className="h-24 w-full overflow-hidden bg-gray-100 dark:bg-gray-700/50 flex items-center justify-center relative">
                                                {sub.sub_product_image ? (
                                                    <img
                                                        src={getFullImageUrl(sub.sub_product_image)}
                                                        alt={sub.sub_product_name}
                                                        className="w-full h-full object-cover"
                                                        onError={(e) => {
                                                            (e.target as HTMLImageElement).style.display = 'none';
                                                            console.error('SubProduct image failed:', sub.sub_product_name, sub.sub_product_image);
                                                        }}
                                                    />
                                                ) : null}
                                            </div>
                                            <div className="p-3 text-center flex flex-col justify-between h-20">
                                                <p className={`text-[11px] font-bold leading-tight ${isSelected ? 'text-orange-500' : 'text-gray-700 dark:text-gray-300'}`}>
                                                    {sub.sub_product_name}
                                                </p>
                                                <p className={`text-[10px] font-black ${isSelected ? 'text-orange-600' : 'text-gray-500'}`}>
                                                    +{currencySymbol}{sub.additional_price.toFixed(0)}
                                                </p>
                                            </div>
                                            {isSelected && (
                                                <div className="absolute top-2 right-2 bg-orange-500 text-white rounded-full p-1 shadow-md">
                                                    <Plus className="w-3 h-3 rotate-45" />
                                                </div>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    <button
                        onClick={handleAdd}
                        className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white py-4 rounded-2xl font-bold text-base flex items-center justify-center gap-2 transition-all shadow-lg shadow-orange-500/30 active:scale-95"
                    >
                        <Plus className="w-5 h-5" /> Agregar al carrito
                    </button>
                </div>
            </div>
        </div>
    );
}
